"use strict";
import { useReadContracts } from "wagmi";
import { getContractAbi, getContractAddress } from "constants/contracts";
import { getCurrentEpochStart, useVeGaugeData } from "features/veSubgraph/gaugeData";
import { useMemo } from "react";
const STALE_TIME = 5 * 6e4;
const GC_TIME = 30 * 6e4;
export function useOnChainGaugeBase(chainId, enabled = true) {
  const voterAddress = getContractAddress(chainId, "Voter");
  const voterAbi = getContractAbi(chainId, "Voter");
  const ve = useVeGaugeData(chainId, enabled);
  const onChainEnabled = enabled && !ve.isTrying;
  const currentTimestamp = useMemo(() => BigInt(Math.floor(Date.now() / 1e3)), []);
  const { data: initialData, isLoading: isLoadingInitial } = useReadContracts({
    contracts: [
      {
        address: voterAddress,
        abi: voterAbi,
        functionName: "length",
        args: []
      },
      {
        address: voterAddress,
        abi: voterAbi,
        functionName: "epochStart",
        args: [currentTimestamp]
      }
    ],
    query: {
      enabled: onChainEnabled && !!voterAddress && !!voterAbi,
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
      refetchOnWindowFocus: false,
      refetchOnMount: false
    }
  });
  const gaugeCount = initialData?.[0]?.status === "success" ? Number(initialData[0].result) : 0;
  const currentEpoch = initialData?.[1]?.status === "success" ? Number(initialData[1].result) : 0;
  const poolContracts = useMemo(() => {
    if (!voterAddress || !voterAbi || gaugeCount === 0) return [];
    return Array.from({ length: gaugeCount }, (_, i) => ({
      address: voterAddress,
      abi: voterAbi,
      functionName: "pools",
      args: [BigInt(i)]
    }));
  }, [voterAddress, voterAbi, gaugeCount]);
  const { data: poolsData, isLoading: isLoadingPools } = useReadContracts({
    contracts: poolContracts,
    query: {
      enabled: poolContracts.length > 0,
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
      refetchOnWindowFocus: false,
      refetchOnMount: false
    }
  });
  const poolAddresses = useMemo(() => {
    if (!poolsData) return [];
    return poolsData.filter((r) => r.status === "success").map((r) => r.result.toLowerCase());
  }, [poolsData]);
  const gaugeContracts = useMemo(() => {
    if (!voterAddress || !voterAbi || poolAddresses.length === 0) return [];
    return poolAddresses.map((poolAddr) => ({
      address: voterAddress,
      abi: voterAbi,
      functionName: "gauges",
      args: [poolAddr]
    }));
  }, [voterAddress, voterAbi, poolAddresses]);
  const { data: gaugesData, isLoading: isLoadingGauges } = useReadContracts({
    contracts: gaugeContracts,
    query: {
      enabled: gaugeContracts.length > 0,
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
      refetchOnWindowFocus: false,
      refetchOnMount: false
    }
  });
  const gaugeAddresses = useMemo(() => {
    if (!gaugesData) return [];
    return gaugesData.filter((r) => r.status === "success").map((r) => r.result.toLowerCase());
  }, [gaugesData]);
  const gaugeDetailContracts = useMemo(() => {
    if (!voterAddress || !voterAbi || gaugeAddresses.length === 0) return [];
    const calls = [];
    gaugeAddresses.forEach((gaugeAddr) => {
      calls.push({
        address: voterAddress,
        abi: voterAbi,
        functionName: "isAlive",
        args: [gaugeAddr]
      });
      calls.push({
        address: voterAddress,
        abi: voterAbi,
        functionName: "gaugeToBribe",
        args: [gaugeAddr]
      });
      calls.push({
        address: voterAddress,
        abi: voterAbi,
        functionName: "gaugeToFees",
        args: [gaugeAddr]
      });
    });
    return calls;
  }, [voterAddress, voterAbi, gaugeAddresses]);
  const { data: gaugeDetailsData, isLoading: isLoadingDetails } = useReadContracts({
    contracts: gaugeDetailContracts,
    query: {
      enabled: gaugeDetailContracts.length > 0,
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
      refetchOnWindowFocus: false,
      refetchOnMount: false
    }
  });
  const gaugeInfoList = useMemo(() => {
    if (!gaugeDetailsData || gaugeAddresses.length === 0 || poolAddresses.length === 0) return [];
    return gaugeAddresses.map((gaugeAddr, i) => {
      const aliveResult = gaugeDetailsData[i * 3];
      const bribeResult = gaugeDetailsData[i * 3 + 1];
      const feeResult = gaugeDetailsData[i * 3 + 2];
      const isAlive = aliveResult?.status === "success" ? aliveResult.result : false;
      const bribeContract = bribeResult?.status === "success" ? bribeResult.result.toLowerCase() : "";
      const feeContract = feeResult?.status === "success" ? feeResult.result.toLowerCase() : "";
      return {
        poolAddress: poolAddresses[i] || "",
        gaugeAddress: gaugeAddr,
        bribeContract,
        feeContract,
        isAlive
      };
    });
  }, [gaugeDetailsData, gaugeAddresses, poolAddresses]);
  return useMemo(() => {
    if (ve.isActive && ve.data) {
      const veGaugeInfoList = ve.data.gauges.map((gauge) => ({
        poolAddress: gauge.pool.id.toLowerCase(),
        gaugeAddress: gauge.id.toLowerCase(),
        bribeContract: gauge.bribeVotingReward.id.toLowerCase(),
        feeContract: gauge.feesVotingReward.id.toLowerCase(),
        isAlive: gauge.isAlive
      }));
      return {
        gaugeCount: veGaugeInfoList.length,
        currentEpoch: ve.data.currentEpoch,
        poolAddresses: veGaugeInfoList.map((g) => g.poolAddress),
        gaugeAddresses: veGaugeInfoList.map((g) => g.gaugeAddress),
        gaugeInfoList: veGaugeInfoList,
        isLoading: false
      };
    }
    if (ve.isTrying) {
      return {
        gaugeCount: 0,
        currentEpoch: getCurrentEpochStart(),
        poolAddresses: [],
        gaugeAddresses: [],
        gaugeInfoList: [],
        isLoading: true
      };
    }
    const isLoading = isLoadingInitial || gaugeCount > 0 && isLoadingPools || poolAddresses.length > 0 && isLoadingGauges || gaugeAddresses.length > 0 && isLoadingDetails;
    return {
      gaugeCount,
      currentEpoch,
      poolAddresses,
      gaugeAddresses,
      gaugeInfoList,
      isLoading
    };
  }, [
    ve.isActive,
    ve.isTrying,
    ve.data,
    isLoadingInitial,
    isLoadingPools,
    isLoadingGauges,
    isLoadingDetails,
    gaugeCount,
    currentEpoch,
    poolAddresses,
    gaugeAddresses,
    gaugeInfoList
  ]);
}
